<template>
    <div class="rounded-lgr border-border border bg-surface-raised px-5 pt-5 pb-3">
        <div v-if="title" class="body-bold text-ink pb-3" :title="tooltip">{{ title }}</div>
        <div class="relative w-full" :class="title ? 'h-[calc(100%-36px)]' : 'h-full'">
            <div class="absolute top-0 left-0 w-full h-full">
                <canvas ref="canvas"></canvas>
            </div>
            <SkeletonLoader v-if="isLoading" class="absolute top-0 left-0 w-full h-full rounded-lgr" />
            <div v-if="!isLoading && !chart" class="absolute top-0 left-0 w-full h-full p-2 flex items-center justify-center text-ink-muted">
                {{ t("analytics.charts.noData") }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import "chartjs-adapter-luxon"; // Needed for TimeScale
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { BarController, BarElement, Chart, type ChartConfiguration, Filler, Legend, LinearScale, LineController, LineElement, PointElement, TimeScale, Tooltip } from "chart.js";
import { onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const {
    chart,
    title = "",
    isLoading = false,
} = defineProps<{
    chart: ChartConfiguration | null;
    title: string;
    isLoading: boolean;
    tooltip?: string;
}>();

const canvasRef = useTemplateRef("canvas");
const chartRef = ref<Chart | null>(null);

const updateChart = (newIsLoading: boolean, newChart: ChartConfiguration | null) => {
    if (!canvasRef.value) {
        return;
    }
    if (chartRef.value) {
        chartRef.value.destroy();
    }
    if (newIsLoading || !newChart) {
        return;
    }
    chartRef.value = new Chart(canvasRef.value, newChart);
};

watch([() => isLoading, () => chart], ([newIsLoading, newChart]) => updateChart(newIsLoading, newChart));

onMounted(() => {
    Chart.register(TimeScale, LinearScale, LineController, PointElement, LineElement, Filler, BarController, BarElement, Legend, Tooltip);
    updateChart(isLoading, chart);
});

onUnmounted(() => {
    chartRef.value?.destroy();
});
</script>
